import { FormEvent, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  createStorageBucket,
  deleteStorageBucket,
  ensureDefaultStorageBucket,
  listStorageBuckets,
  StorageBucket,
} from '../../services/storageApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';

export function StorageBucketsPanel() {
  const { canWriteModule } = usePermissions();
  const canWrite = canWriteModule('storage');
  const [buckets, setBuckets] = useState<StorageBucket[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [ensuring, setEnsuring] = useState(false);
  const [deletingName, setDeletingName] = useState<string | null>(null);
  const [newBucketName, setNewBucketName] = useState('');

  const loadBuckets = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      setBuckets(await listStorageBuckets());
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao listar buckets.'));
      setBuckets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBuckets();
  }, [loadBuckets]);

  async function handleCreate(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const name = newBucketName.trim();
    if (!name) {
      toast.error('Informe o nome do bucket.');
      return;
    }

    setCreating(true);
    try {
      await createStorageBucket({ name });
      setNewBucketName('');
      toast.success('Bucket criado.');
      await loadBuckets();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao criar bucket.'));
    } finally {
      setCreating(false);
    }
  }

  async function handleEnsureDefault(): Promise<void> {
    if (!canWrite) return;
    setEnsuring(true);
    try {
      await ensureDefaultStorageBucket();
      toast.success('Bucket padrão garantido.');
      await loadBuckets();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao garantir o bucket padrão.'));
    } finally {
      setEnsuring(false);
    }
  }

  async function handleDelete(bucket: StorageBucket): Promise<void> {
    if (!canWrite) return;

    if (!window.confirm(`Excluir o bucket "${bucket.name}"? Esta ação não pode ser desfeita.`)) {
      return;
    }

    setDeletingName(bucket.name);
    try {
      await deleteStorageBucket(bucket.name, false);
      toast.success('Bucket excluído.');
      await loadBuckets();
    } catch (error) {
      const message = getRequestErrorMessage(error, 'Falha ao excluir bucket.');
      const force = window.confirm(
        `${message}\n\nDeseja forçar a exclusão, removendo também o conteúdo?`,
      );
      if (!force) {
        return;
      }

      try {
        await deleteStorageBucket(bucket.name, true);
        toast.success('Bucket excluído.');
        await loadBuckets();
      } catch (forceError) {
        toast.error(getRequestErrorMessage(forceError, 'Falha ao excluir bucket.'));
      }
    } finally {
      setDeletingName(null);
    }
  }

  if (loading) {
    return <LoadingSpinner label="Carregando buckets..." />;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Buckets</h3>
            <p className="mt-1 text-sm text-slate-500">
              Gerencie buckets do armazenamento S3-compatível. O bucket padrão é o definido na
              conexão da empresa.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="erp-btn-secondary erp-btn-sm"
              onClick={() => void loadBuckets()}
            >
              Atualizar
            </button>
            {canWrite ? (
              <button
                type="button"
                className="erp-btn-secondary erp-btn-sm"
                disabled={ensuring}
                onClick={() => void handleEnsureDefault()}
              >
                {ensuring ? 'Garantindo...' : 'Garantir bucket padrão'}
              </button>
            ) : null}
          </div>
        </div>

        {canWrite ? (
          <form onSubmit={(event) => void handleCreate(event)} className="mb-4 flex flex-wrap gap-2">
            <input
              type="text"
              value={newBucketName}
              onChange={(event) => setNewBucketName(event.target.value)}
              placeholder="nome-do-bucket"
              className="min-w-[220px] flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <button type="submit" className="erp-btn-primary erp-btn-sm" disabled={creating}>
              {creating ? 'Criando...' : 'Criar bucket'}
            </button>
          </form>
        ) : (
          <p className="mb-4 text-xs text-slate-500">
            Criar, garantir padrão e excluir exigem permissão de escrita em Armazenamento.
          </p>
        )}

        {buckets.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhum bucket encontrado.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-2 font-medium">Nome</th>
                  <th className="px-2 py-2 font-medium">Criado em</th>
                  <th className="px-2 py-2 font-medium">Padrão</th>
                  <th className="px-2 py-2 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {buckets.map((bucket) => (
                  <tr key={bucket.name} className="border-b border-slate-100">
                    <td className="px-2 py-2 font-mono text-slate-800">{bucket.name}</td>
                    <td className="px-2 py-2 text-slate-600">
                      {bucket.creationDateUtc
                        ? new Date(bucket.creationDateUtc).toLocaleString('pt-BR')
                        : '—'}
                    </td>
                    <td className="px-2 py-2">
                      {bucket.isDefault ? (
                        <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800">
                          Sim
                        </span>
                      ) : (
                        <span className="text-slate-500">Não</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {canWrite ? (
                        <button
                          type="button"
                          className="erp-btn-danger erp-btn-sm"
                          disabled={deletingName === bucket.name}
                          onClick={() => void handleDelete(bucket)}
                        >
                          {deletingName === bucket.name ? 'Excluindo...' : 'Excluir'}
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
